import React, { createContext, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { supabase } from '../integrations/supabase/client';
import { requestMagicLink, requestSignupMagicLink } from '../features/auth/api';
import { bootstrapUserData } from '../lib/bootstrap';
import { useQueryClient } from '@tanstack/react-query';

type Session = import('@supabase/supabase-js').Session | null;
type User = import('@supabase/supabase-js').User | null;

type AuthState = {
  user: User;
  session: Session;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  sendMagicLink: (email: string) => Promise<{ error: Error | null }>;
  sendSignupMagicLink: (email: string) => Promise<{ error: Error | null }>;
};

const Ctx = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session>(null);
  const [loading, setLoading] = useState(true);
  const bootstrappedRef = useRef<Set<string>>(new Set());
  const queryClient = useQueryClient();

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session ?? null);
      setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((event, sess) => {
      setSession(sess);

      // A recovery link may land on any page (e.g. /auth/callback or /).
      // Always send the person to the set-new-password step.
      if (event === 'PASSWORD_RECOVERY' && window.location.pathname !== '/auth/reset') {
        window.location.replace('/auth/reset?type=recovery');
        return;
      }
      
      // Bootstrap user data only on SIGNED_IN to ensure fresh token
      if (event === 'SIGNED_IN' && sess?.user && !bootstrappedRef.current.has(sess.user.id)) {
        bootstrappedRef.current.add(sess.user.id);
        bootstrapUserData(sess.user.id, sess.user.email || '', sess.user.user_metadata);
      }

      // Sync profiles.email and Stripe customer email when user confirms a new email
      if (event === 'USER_UPDATED' && sess?.user?.email) {
        const userId = sess.user.id;
        const confirmedEmail = sess.user.email;

        // Update profiles.email in Supabase
        supabase
          .from('profiles')
          .update({ email: confirmedEmail })
          .eq('user_id', userId)
          .then(({ error }) => {
            if (error) {
              console.error('[AuthContext] Failed to sync profiles.email:', error);
            } else {
              // Invalidate React Query cache so UI reflects new email
              queryClient.invalidateQueries({ queryKey: ['profile'] });
            }
          });

        // Best-effort Stripe customer email sync (non-blocking)
        supabase.functions.invoke('sync-stripe-email', { body: {} }).then(({ error }) => {
          if (error) {
            console.error('[AuthContext] sync-stripe-email failed (non-critical):', error);
          }
        });
      }
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        supabase.auth.getSession().then(({ data }) => {
          if (data.session) {
            setSession(data.session);
            queryClient.invalidateQueries({ queryKey: ['profile'] });
          }
        });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mounted = false;
      subscription.subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [queryClient]);

  const value = useMemo<AuthState>(() => ({
    user: session?.user ?? null,
    session,
    loading,
    signIn: async (email, password) => {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return { error };
    },
    signUp: async (email, password) => {
      // With email confirmations ON, this returns pending user; app should show "check your email"
      const { error } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          emailRedirectTo: `${(import.meta as any).env?.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '')}/auth/callback?next=/dashboard`
        }
      });
      return { error };
    },
    signOut: async () => { await supabase.auth.signOut(); },
    sendMagicLink: async (email) => {
      try {
        await requestMagicLink(email);
        return { error: null };
      } catch (error) {
        return { error: error as Error };
      }
    },
    sendSignupMagicLink: async (email) => {
      try {
        await requestSignupMagicLink(email);
        return { error: null };
      } catch (error) {
        return { error: error as Error };
      }
    },
  }), [session, loading]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}